<template>
    <div class="tile stack-m">
        <div class="tile-content">
            <div class="tile-subtitle">
                <h3>Conditions</h3>
            </div>
            <div v-if="hasCriteria">
                <condition-summary :criteria="rule.criteria" :field-provider="fieldProvider"/>
            </div>
            <div v-else>This rule matches all cases.</div>
        </div>
        <div class="tile-content tile-content-secondary" v-for="action in rule.actions" :key="action.$internalId">
            <div class="tile-subtitle">
                <h3>{{action.name | unKebabCase}}</h3>
                <r-badge class="no-flex" :type="getStateStyle(action)">{{getStateLabel(action)}}</r-badge>
            </div>
            <component :is="getSummaryComponent(action)" :action="action" :event="event"/>
        </div>
    </div>
</template>

<script>
    import AddNsfFeeSummary from '@/modules/rules-engine/actions/add-nsf-fee/add-nsf-fee-summary';
    import AddRiskScoreSummary from '@/modules/rules-engine/actions/add-risk-score/add-risk-score-summary';
    import BlacklistSummary from '@/modules/rules-engine/actions/blacklist/blacklist-summary';
    import BlankSummary from '@/modules/rules-engine/actions/blank-summary';
    import CancelScheduledPaymentsSummary
        from '@/modules/rules-engine/actions/cancel-scheduled-payments/cancel-scheduled-payments-summary';
    import ConditionSummary from '@/modules/rules-engine/components/condition-summary/condition-summary';
    import DisplayOtherChoicesSummary
        from '@/modules/rules-engine/actions/display-other-choices/display-other-choices-summary';
    import EventModel from '@/common/models/rules/event-model';
    import FieldProvider from '@/modules/rules-engine/components/condition-builder/field-provider';
    import getFieldsFromPathways from '@/modules/rules-engine/components/condition-builder/get-fields-from-pathways';
    import GuessPaymentCardExpirationSummary
        from '@/modules/rules-engine/actions/guess-payment-card-expiration/guess-payment-card-expiration-summary';
    import OfferPurchaseBumpSummary
        from '@/modules/rules-engine/actions/offer-purchase-bump/offer-purchase-bump-summary';
    import PickGatewayAccountSummary
        from '@/modules/rules-engine/actions/pick-gateway-account/pick-gateway-account-summary';
    import RequestKYCSummary from '@/modules/rules-engine/actions/request-kyc/request-kyc-summary';
    import resolveFilterType from '@/components/query/helpers/resolve-filter-type';
    import RuleActionModel from '@/common/models/rules/actions/rule-action-model';
    import RuleModel from '@/common/models/rules/rule-model';
    import ScheduleInvoiceRetrySummary
        from '@/modules/rules-engine/actions/schedule-invoice-retry/schedule-invoice-retry-summary';
    import SchedulePaymentRetrySummary
        from '@/modules/rules-engine/actions/schedule-payment-retry/schedule-payment-retry-summary';
    import SchedulePaymentSummary from '@/modules/rules-engine/actions/schedule-payment/schedule-payment-summary';
    import SendEmailSummary from '@/modules/rules-engine/actions/send-email/send-email-summary';
    import ShowDescriptorDisclaimerSummary
        from '@/modules/rules-engine/actions/show-descriptor-disclaimer/show-descriptor-disclaimer-summary';
    import StopSubscriptionsSummary from '@/modules/rules-engine/actions/stop-subscriptions/stop-subscriptions-summary';
    import ChurnSubscriptionSummary from '@/modules/rules-engine/actions/churn-subscription/churn-subscription-summary';
    import TagOrUntagCustomerSummary
        from '@/modules/rules-engine/actions/tag-or-untag-customer/tag-or-untag-customer-summary';
    import TriggerWebhookSummary from '@/modules/rules-engine/actions/trigger-webhook/trigger-webhook-summary';
    import ScheduleReminderSummary from '@/modules/rules-engine/actions/schedule-reminder/schedule-reminder-summary';
    import ResetReminderSummary from '@/modules/rules-engine/actions/reset-reminder/reset-reminder-summary';
    import RemoveReminderSummary from '@/modules/rules-engine/actions/remove-reminder/remove-reminder-summary';

    export default {
        name: 'rule-summary',
        components: {ConditionSummary},
        props: {
            rule: {
                type: RuleModel,
            },
            event: {
                type: EventModel,
            },
        },
        computed: {
            hasCriteria() {
                return this.rule.criteria !== null;
            },
            fieldProvider() {
                return new FieldProvider({
                    pathwayResolver: getFieldsFromPathways,
                    typeResolver: resolveFilterType,
                    pathways: this.event.pathways,
                    customFieldsStore: this.$store.getters['rulesEngine/customFields'],
                });
            },
        },
        methods: {
            getStateStyle(action) {
                return action.status ? 'positive' : 'negative';
            },
            getStateLabel(action) {
                return action.status ? 'Active' : 'Inactive';
            },
            getSummaryComponent(action) {
                switch (action.name) {
                    case RuleActionModel.Names.addRiskScore:
                        return AddRiskScoreSummary;
                    case RuleActionModel.Names.blacklist:
                        return BlacklistSummary;
                    case RuleActionModel.Names.pickGatewayAccount:
                        return PickGatewayAccountSummary;
                    case RuleActionModel.Names.requestKyc:
                        return RequestKYCSummary;
                    case RuleActionModel.Names.schedulePayment:
                        return SchedulePaymentSummary;
                    case RuleActionModel.Names.schedulePaymentRetry:
                        return SchedulePaymentRetrySummary;
                    case RuleActionModel.Names.sendEmail:
                        return SendEmailSummary;
                    case RuleActionModel.Names.triggerWebhook:
                        return TriggerWebhookSummary;
                    case RuleActionModel.Names.stopSubscriptions:
                        return StopSubscriptionsSummary;
                    case RuleActionModel.Names.churnSubscription:
                        return ChurnSubscriptionSummary;
                    case RuleActionModel.Names.cancelScheduledPayments:
                        return CancelScheduledPaymentsSummary;
                    case RuleActionModel.Names.guessPaymentCardExpiration:
                        return GuessPaymentCardExpirationSummary;
                    case RuleActionModel.Names.addNsfFee:
                        return AddNsfFeeSummary;
                    case RuleActionModel.Names.showDescriptorDisclaimer:
                        return ShowDescriptorDisclaimerSummary;
                    case RuleActionModel.Names.offerPurchaseBump:
                        return OfferPurchaseBumpSummary;
                    case RuleActionModel.Names.scheduleInvoiceRetry:
                        return ScheduleInvoiceRetrySummary;
                    case RuleActionModel.Names.tagOrUntagCustomer:
                        return TagOrUntagCustomerSummary;
                    case RuleActionModel.Names.displayOtherChoices:
                        return DisplayOtherChoicesSummary;
                    case RuleActionModel.Names.scheduleReminder:
                        return ScheduleReminderSummary;
                    case RuleActionModel.Names.resetReminder:
                        return ResetReminderSummary;
                    case RuleActionModel.Names.removeReminder:
                        return RemoveReminderSummary;
                    default:
                        return BlankSummary;
                }
            },
        },
    };
</script>
